// Home.tsx – Simple one-page
import React, { useState, useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import DefaultLayout from '../components/layouts/DefaultLayout'
import {
  Box,
  Button,
  Text,
  VStack,
  HStack,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  Input,
  InputGroup,
  InputRightElement,
  useToast,
  useDisclosure,
  Spinner,
  FormControl,
  FormLabel,
  Stack,
  Badge,
  Icon,
  Divider,
  Switch,
  Slider,
  SliderTrack,
  SliderFilledTrack,
  SliderThumb,
  IconButton,
  Wrap,
  WrapItem
} from '@chakra-ui/react'
import {
  LockIcon,
  CheckCircleIcon,
  CloseIcon,
  SettingsIcon,
  AddIcon,
  ViewIcon,
  ViewOffIcon,
  RepeatIcon
} from '@chakra-ui/icons'
import { FaTelegram } from 'react-icons/fa'
import { FiSettings } from 'react-icons/fi'
import { useRecoilState } from 'recoil'
import { authState } from '../atoms/auth'
import {
  signIn,
  signOut,
  updateTrading,
  updateKeyAndSecret,
  getSymbolUsdtPrice
} from '../services/apiService'
import { useAuthState } from '../utils/authUtils'
import ApiManual from '../components/ApiManual'

type RoiTrigger = {
  roi: string
  size: string
}

const Home: React.FC = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [auth, setAuth] = useRecoilState(authState)
  const { reloadUser, initUser } = useAuthState()
  const toast = useToast()

  // 로그인 모달 상태
  const {
    isOpen: isLoginOpen,
    onOpen: onLoginOpen,
    onClose: onLoginClose
  } = useDisclosure()

  // API 관리 모달 상태
  const {
    isOpen: isApiOpen,
    onOpen: onApiOpen,
    onClose: onApiClose
  } = useDisclosure()

  // 시드 입력 모달 상태
  const {
    isOpen: isSeedOpen,
    onOpen: onSeedOpen,
    onClose: onSeedClose
  } = useDisclosure()

  // 로그인 입력값
  const [account, setAccount] = useState('')
  const [password, setPassword] = useState('')
  const [isLoggingIn, setIsLoggingIn] = useState(false)

  // API 관리 입력값
  const [apiKey, setApiKey] = useState('')
  const [apiSecret, setApiSecret] = useState('')
  const [showSecret, setShowSecret] = useState(false)

  // 시드 입력값
  const [seedAmount, setSeedAmount] = useState('')

  // 수동 설정 입력값 (관리자용)
  const [useManualConfig, setUseManualConfig] = useState(false) // 수동 설정 사용 여부
  const [manualLeverage, setManualLeverage] = useState('30')
  const [manualTakeProfit, setManualTakeProfit] = useState('10')
  const [manualStopLoss, setManualStopLoss] = useState('0')
  const [manualSize, setManualSize] = useState('1')
  const [manualDcaCount, setManualDcaCount] = useState('50')
  const [manualRoi, setManualRoi] = useState('-30')
  const [manualRoiTriggers, setManualRoiTriggers] = useState<RoiTrigger[]>([])

  // 사이즈 계산기 상태
  const [showCalculator, setShowCalculator] = useState(false)
  const [calcSeed, setCalcSeed] = useState('1000')
  const [calcSeedPercent, setCalcSeedPercent] = useState(100) // 시드 사용 비율 (%)
  const [calcDivide, setCalcDivide] = useState('50')
  const [xrpPrice, setXrpPrice] = useState('0.0000')

  // 자동매매 시작/종료 상태
  const [isTradingLoading, setIsTradingLoading] = useState(false)

  // 관리자 체크
  const isAdmin = useMemo(() => auth?.isAdmin === 1, [auth?.isAdmin])

  // 현재 자동매매 상태 확인
  const currentTrading = useMemo(() => {
    if (!auth?.id || !Array.isArray(auth?.trading)) return null
    // XRP 우선, 없으면 첫 번째
    const xrpTrading = auth.trading.find((t) => t.symbol === 'XRP')
    return xrpTrading || auth.trading[0] || null
  }, [auth])

  const isAutoTradingRunning = useMemo(() => {
    return currentTrading?.botStatus === 1
  }, [currentTrading])

  const selectedSymbol = useMemo(() => {
    return currentTrading?.symbol || 'XRP'
  }, [currentTrading])

  // API 키 초기화
  useEffect(() => {
    if (auth?.apiKey) setApiKey(auth.apiKey)
    if (auth?.apiSecret) setApiSecret(auth.apiSecret)
  }, [auth])

  // 페이지 로드 시 사용자 정보 확인
  useEffect(() => {
    if (auth?.id) {
      reloadUser().catch(() => {
        // 에러 무시
      })
    }
  }, [])

  // 로그인 처리
  const handleLogin = async () => {
    if (!account.trim() || !password.trim()) {
      toast({
        title: t('Input Error'),
        description: t('Please enter your account and password.'),
        status: 'warning',
        duration: 2000,
        isClosable: true
      })
      return
    }

    setIsLoggingIn(true)
    try {
      const { data: res } = await signIn({
        account: account.trim(),
        password: password.trim()
      })
      if (res.statusCode === 200) {
        setAuth({
          ...res.data
        })
        toast({
          title: t('Login Successful'),
          description: t('Welcome!'),
          status: 'success',
          duration: 2000,
          isClosable: true
        })
        onLoginClose()
        setAccount('')
        setPassword('')
        await reloadUser()
      }
    } catch (err: any) {
      toast({
        title: t('Login Failed'),
        description: t('Account or password does not match.'),
        status: 'error',
        duration: 3000,
        isClosable: true
      })
    } finally {
      setIsLoggingIn(false)
    }
  }

  // API 저장 처리
  const handleSaveApi = async () => {
    try {
      const { data: res } = await updateKeyAndSecret({
        key: apiKey,
        secret: apiSecret
      })
      if (res.statusCode === 200) {
        toast({
          title: t('API Management'),
          description: res.message,
          status: 'success',
          duration: 2000,
          isClosable: true
        })
        onApiClose()
        await reloadUser()
      }
    } catch (err: any) {
      toast({
        title: t('API Management'),
        description:
          err.response?.data?.message || t('An unknown error occurred.'),
        status: 'error',
        duration: 3000,
        isClosable: true
      })
    }
  }

  // 자동매매 시작 - 관리자는 수동 설정, 일반 유저는 시드 입력
  const handleStartAutoTrading = () => {
    if (!auth?.id) {
      toast({
        title: t('Login Required'),
        description: t('Please login first to start auto trading.'),
        status: 'warning',
        duration: 2000,
        isClosable: true
      })
      onLoginOpen()
      return
    }

    if (!currentTrading) {
      toast({
        title: t('Settings Required'),
        description: t('No auto trading settings found. Please complete settings first.'),
        status: 'warning',
        duration: 2000,
        isClosable: true
      })
      return
    }

    // 시드 입력 모달 열기 (관리자는 스위치 옵션 포함)
    setSeedAmount('')
    setUseManualConfig(false) // 기본값은 시드 입력 방식

    // 관리자인 경우 수동 설정 기본값 불러오기
    if (isAdmin) {
      setManualLeverage(String(currentTrading.leverage || 30))
      setManualTakeProfit(String(currentTrading.tp || 10))
      setManualStopLoss(String(currentTrading.sl || 0))
      setManualSize(String(currentTrading.size || 1))
      setManualDcaCount(String(currentTrading.addOrder || 50))
      setManualRoi(String(currentTrading.dynamic?.[0]?.roi || -30))
      
      // 물타기 설정 불러오기
      if (Array.isArray(currentTrading.dynamic) && currentTrading.dynamic.length > 0) {
        const roiList = currentTrading.dynamic.map((item) => ({
          roi: String(item.roi),
          size: String(item.size)
        }))
        setManualRoiTriggers(roiList)
      } else {
        // 기본값으로 50개의 -30% ROI 트리거 생성
        const defaultTriggers = Array.from({ length: 50 }, () => ({
          roi: '-30',
          size: String(currentTrading.size || 1)
        }))
        setManualRoiTriggers(defaultTriggers)
      }
    }

    onSeedOpen()
  }

  // XRP 가격 조회 (수동 설정 모드일 때)
  useEffect(() => {
    const fetchXrpPrice = async () => {
      if (!isSeedOpen || !useManualConfig || !isAdmin) return

      try {
        const { data: priceRes } = await getSymbolUsdtPrice('XRP')
        if (priceRes.statusCode === 200 && priceRes.data) {
          const price = Number(priceRes.data)
          setXrpPrice(price.toFixed(4))
        }
      } catch (error) {
        console.error('XRP 가격 조회 실패:', error)
      }
    }

    fetchXrpPrice()
  }, [isSeedOpen, useManualConfig, isAdmin])

  // 사이즈 계산 로직
  const calculatedSize = useMemo(() => {
    const seed = Number(calcSeed) || 0
    const seedPercent = Number(calcSeedPercent) || 0
    const leverage = Number(manualLeverage) || 0
    const divide = Number(calcDivide) || 1
    const price = Number(xrpPrice) || 0

    if (seed === 0 || leverage === 0 || divide === 0 || price === 0) {
      return 0
    }

    // 비율 적용된 시드
    const usedSeed = seed * (seedPercent / 100)
    // 회차당 실제 진입 금액
    const entryAmount = usedSeed / divide
    // 레버리지 적용 금액
    const leveragedAmount = entryAmount * leverage
    // 코인 수량
    const coinAmount = leveragedAmount / price
    // Size 계산 (XRP는 1 size = 10 XRP)
    const sizeUnits = Math.floor(coinAmount / 10)

    return sizeUnits
  }, [calcSeed, calcSeedPercent, manualLeverage, calcDivide, xrpPrice])

  // 계산된 사이즈 적용
  const handleApplyCalculatedSize = () => {
    setManualSize(String(calculatedSize))
    setShowCalculator(false)
    toast({
      title: t('Size Applied'),
      description: t('Calculated size {{size}} has been applied.', { size: calculatedSize }),
      status: 'success',
      duration: 2000,
      isClosable: true
    })
  }

  // 수동 물타기 단계 추가
  const addManualRoiTrigger = () => {
    const last = manualRoiTriggers[manualRoiTriggers.length - 1] || { roi: '-30', size: '1' }
    setManualRoiTriggers([...manualRoiTriggers, { roi: last.roi, size: last.size }])
  }

  // 수동 물타기 단계 삭제
  const removeManualRoiTrigger = (index: number) => {
    const updated = [...manualRoiTriggers]
    updated.splice(index, 1)
    setManualRoiTriggers(updated)
  }

  // 수동 물타기 일괄 설정
  const handleBatchSetupManual = () => {
    const count = Math.max(1, Number(manualDcaCount))
    const roi = Number(manualRoi)
    const size = Number(manualSize)
    const triggers = Array.from({ length: count }, () => ({ roi: String(roi), size: String(size) }))
    setManualRoiTriggers(triggers)
    toast({
      title: t('Batch Setup Complete'),
      description: t('{{count}} DCA steps have been created.', { count }),
      status: 'success',
      duration: 2000,
      isClosable: true
    })
  }


  // 시드 입력 후 실제 자동매매 시작
  const handleConfirmStartWithSeed = async () => {
    if (!seedAmount || isNaN(Number(seedAmount)) || Number(seedAmount) <= 0) {
      toast({
        title: t('Input Error'),
        description: t('Please enter a valid seed amount.'),
        status: 'warning',
        duration: 2000,
        isClosable: true
      })
      return
    }

    setIsTradingLoading(true)
    try {
      const totalSeed = Number(seedAmount)
      const useSeedPercent = 20 // 20%
      const availableSeed = totalSeed * (useSeedPercent / 100) // 입력 시드의 20%
      const dcaCount = 100 // 물타기 회수
      const roi = -30 // ROI -30%
      const leverage = currentTrading?.leverage || 30 // 기본 레버리지 30
      const tp = 10 // TP 10%
      const sl = 0 // 손절 비활성화
      const symbol = 'XRP'

      // XRP 가격 조회
      const { data: priceRes } = await getSymbolUsdtPrice(symbol)
      if (priceRes.statusCode !== 200 || !priceRes.data) {
        throw new Error('XRP 가격 조회에 실패했습니다.')
      }
      const xrpPrice = Number(priceRes.data)

      if (!xrpPrice || xrpPrice <= 0) {
        throw new Error('유효하지 않은 XRP 가격입니다.')
      }

      // 사이즈 계산 (Strategy.tsx의 로직 참고)
      // 회차당 마진
      const margin = availableSeed / dcaCount // 사용 시드를 DCA 100회로 분할
      // 가치 = 마진 * 레버리지
      const value = margin * leverage // 회차당 명목가 = 마진 * 레버리지
      // 코인 수량 = 가치 / 가격
      const coinAmount = value / xrpPrice
      // Size 계산: 코인 수량 / (1 Size 단위), 내림
      // XRP는 1 Size = 10 XRP
      const unit = 10 // XRP는 10 XRP가 1 size
      const sizeUnits = Math.floor(coinAmount / unit)

      if (sizeUnits < 1) {
        toast({
          title: t('Insufficient Seed'),
          description: t(
            'Minimum seed amount is insufficient. Please enter a larger amount.'
          ),
          status: 'warning',
          duration: 3000,
          isClosable: true
        })
        setIsTradingLoading(false)
        return
      }

      // 물타기 dynamic 배열 생성 (100개, 각각 roi: -30, size: 계산된 값)
      const roiTriggers = Array.from({ length: dcaCount }, () => ({
        roi,
        size: sizeUnits
      }))

      // XRP 트레이딩 업데이트 및 봇 시작
      const { data: res } = await updateTrading({
        symbol: 'XRP',
        botStatus: true,
        leverage,
        takeProfit: tp,
        stopLoss: sl,
        size: sizeUnits,
        direction: 0, // 롱숏 둘다 허용
        roiTriggers,
        useStopLoss: false
      })

      if (res.statusCode === 200) {
        toast({
          title: t('Auto Trading Started'),
          description: res.message || t('Auto trading has been started.'),
          status: 'success',
          duration: 2000,
          isClosable: true
        })
        onSeedClose()
        setSeedAmount('')
        await reloadUser()
      }
    } catch (err: any) {
      toast({
        title: t('Auto Trading Start Failed'),
        description: t('An unknown error occurred.'),
        status: 'error',
        duration: 3000,
        isClosable: true
      })
    } finally {
      setIsTradingLoading(false)
    }
  }

  // 관리자용 수동 설정으로 자동매매 시작
  const handleConfirmStartWithManual = async () => {
    setIsTradingLoading(true)
    try {
      const leverage = Number(manualLeverage)
      const tp = Number(manualTakeProfit)
      const sl = Number(manualStopLoss)
      const size = Number(manualSize)

      // 물타기 설정: manualRoiTriggers 배열 사용
      const roiTriggers = manualRoiTriggers.map((item) => ({
        roi: Number(item.roi),
        size: Number(item.size)
      }))

      // XRP 트레이딩 업데이트 및 봇 시작
      const { data: res } = await updateTrading({
        symbol: 'XRP',
        botStatus: true,
        leverage,
        takeProfit: tp,
        stopLoss: sl,
        size,
        direction: 0, // 롱숏 둘다 허용
        roiTriggers,
        useStopLoss: sl > 0
      })

      if (res.statusCode === 200) {
        toast({
          title: t('Auto Trading Started'),
          description: res.message || t('Auto trading has been started.'),
          status: 'success',
          duration: 2000,
          isClosable: true
        })
        onSeedClose()
        await reloadUser()
      }
    } catch (err: any) {
      toast({
        title: t('Auto Trading Start Failed'),
        description: t('An unknown error occurred.'),
        status: 'error',
        duration: 3000,
        isClosable: true
      })
    } finally {
      setIsTradingLoading(false)
    }
  }

  // 자동매매 종료
  const handleStopAutoTrading = async () => {
    if (!auth?.id || !currentTrading) {
      return
    }

    setIsTradingLoading(true)
    try {
      const { data: res } = await updateTrading({
        symbol: selectedSymbol,
        botStatus: false
      })

      if (res.statusCode === 200) {
        toast({
          title: t('Auto Trading Stopped'),
          description: res.message || t('Auto trading has been stopped.'),
          status: 'success',
          duration: 2000,
          isClosable: true
        })
        await reloadUser()
      }
    } catch (err: any) {
      toast({
        title: t('Auto Trading Stop Failed'),
        description: t('An unknown error occurred.'),
        status: 'error',
        duration: 3000,
        isClosable: true
      })
    } finally {
      setIsTradingLoading(false)
    }
  }

  // 로그아웃 처리
  const handleLogout = async () => {
    try {
      await signOut()
      await initUser()
      toast({
        title: t('Logout'),
        description: t('You have been logged out.'),
        status: 'success',
        duration: 2000,
        isClosable: true
      })
    } catch (err: any) {
      // 서버 에러가 있어도 로컬 상태는 초기화
      await initUser()
      toast({
        title: t('Logout'),
        description: t('You have been logged out.'),
        status: 'success',
        duration: 2000,
        isClosable: true
      })
    }
  }

  return (
    <DefaultLayout>
      <Box
        minH="100vh"
        bg="gray.50"
        px={{ base: 4, md: 6 }}
        py={{ base: 8, md: 12 }}
        display="flex"
        alignItems="center"
        justifyContent="center"
      >
        <Box maxW="2xl" w="full">
          {/* 로그인 상태에 따라 다른 레이아웃 */}
          {auth?.id ? (
            /* 로그인 후 - 컴팩트한 레이아웃 */
            <Box
              bg="white"
              borderRadius="2xl"
              boxShadow="xl"
              p={{ base: 6, md: 8 }}
            >
              <VStack spacing={5} align="stretch">
                {/* 헤더 - 최소화 */}
                <HStack
                  justify="space-between"
                  align="center"
                  pb={3}
                  borderBottom="1px solid"
                  borderColor="gray.200"
                >
                  <Text
                    fontSize={{ base: 'xl', md: '2xl' }}
                    fontWeight="bold"
                    color="gray.800"
                  >
                    FreeRipple
                  </Text>
                  <Badge
                    colorScheme={isAutoTradingRunning ? 'green' : 'gray'}
                    fontSize={{ base: 'sm', md: 'md' }}
                    px={4}
                    py={1.5}
                    borderRadius="full"
                    fontWeight="semibold"
                  >
                    {isAutoTradingRunning ? (
                      <HStack spacing={1.5}>
                        <Icon as={CheckCircleIcon} boxSize={4} />
                        <Text>{t('Running')}</Text>
                      </HStack>
                    ) : (
                      <HStack spacing={1.5}>
                        <Icon as={CloseIcon} boxSize={3} />
                        <Text>{t('Stopped')}</Text>
                      </HStack>
                    )}
                  </Badge>
                </HStack>

                {/* 로그인 사용자 정보 */}
                <Box
                  bg="gray.50"
                  borderRadius="lg"
                  p={3}
                  borderWidth="1px"
                  borderColor="gray.200"
                >
                  <VStack spacing={1} align="stretch">
                    <Text fontSize="sm" color="gray.600" fontWeight="semibold">
                      Login Info
                    </Text>
                    <HStack spacing={2}>
                      <Text fontSize="sm" color="gray.700">
                        Account:
                      </Text>
                      <Text fontSize="sm" color="gray.900" fontWeight="bold">
                        {auth?.account || '-'}
                      </Text>
                    </HStack>
                    <HStack spacing={2}>
                      <Text fontSize="sm" color="gray.700">
                        Name:
                      </Text>
                      <Text fontSize="sm" color="gray.900" fontWeight="bold">
                        {auth?.username || '-'}
                      </Text>
                    </HStack>
                  </VStack>
                </Box>

                {/* 물타기 진행 상황 */}
                {isAutoTradingRunning && (
                  <Box
                    bg="blue.50"
                    borderRadius="lg"
                    p={3}
                    borderWidth="1px"
                    borderColor="blue.200"
                  >
                    <VStack spacing={1} align="stretch">
                      <HStack justify="space-between">
                        <Text fontSize="sm" color="blue.600" fontWeight="semibold">
                          DCA Progress
                        </Text>
                        <IconButton
                          aria-label="Refresh DCA"
                          icon={<RepeatIcon />}
                          size="xs"
                          variant="ghost"
                          colorScheme="blue"
                          onClick={() => reloadUser()}
                        />
                      </HStack>
                      <HStack spacing={2}>
                        <Text fontSize="sm" color="gray.700">
                          Long Position:
                        </Text>
                        <Text fontSize="sm" color="green.600" fontWeight="bold">
                          {currentTrading?.currentAddOrderLong || 0} times
                        </Text>
                      </HStack>
                      <HStack spacing={2}>
                        <Text fontSize="sm" color="gray.700">
                          Short Position:
                        </Text>
                        <Text fontSize="sm" color="red.600" fontWeight="bold">
                          {currentTrading?.currentAddOrderShort || 0} times
                        </Text>
                      </HStack>
                    </VStack>
                  </Box>
                )}

                {/* 자동매매 시작/종료 버튼 - 메인 */}
                <Button
                  h={{ base: '60px', md: '70px' }}
                  w="full"
                  fontSize={{ base: 'xl', md: '2xl' }}
                  fontWeight="bold"
                  colorScheme={isAutoTradingRunning ? 'red' : 'green'}
                  onClick={
                    isAutoTradingRunning
                      ? handleStopAutoTrading
                      : handleStartAutoTrading
                  }
                  isLoading={isTradingLoading}
                  loadingText={
                    isAutoTradingRunning ? t('종료 중...') : t('시작 중...')
                  }
                  disabled={!currentTrading}
                  borderRadius="xl"
                  boxShadow="lg"
                  _hover={{
                    transform: 'scale(1.02)',
                    boxShadow: 'xl'
                  }}
                  transition="all 0.2s"
                  leftIcon={
                    isAutoTradingRunning ? (
                      <Icon as={CloseIcon} boxSize={6} />
                    ) : (
                      <Icon as={CheckCircleIcon} boxSize={6} />
                    )
                  }
                >
                  {isAutoTradingRunning
                    ? t('Stop Auto Trading')
                    : t('Start Auto Trading')}
                </Button>

                {/* 하단 메뉴 버튼들 */}
                <VStack spacing={3} align="stretch" pt={2}>
                  <Button
                    h={{ base: '52px', md: '60px' }}
                    w="full"
                    fontSize={{ base: 'md', md: 'lg' }}
                    fontWeight="semibold"
                    variant="outline"
                    colorScheme="purple"
                    onClick={onApiOpen}
                    borderRadius="xl"
                    leftIcon={<Icon as={SettingsIcon} boxSize={5} />}
                    _hover={{
                      bg: 'purple.50',
                      transform: 'scale(1.01)'
                    }}
                    transition="all 0.2s"
                  >
                    {t('API Management')}
                  </Button>

                  {/* 관리자 페이지 버튼 - 관리자만 표시 */}
                  {auth?.isAdmin === 1 && (
                    <Button
                      h={{ base: '52px', md: '60px' }}
                      w="full"
                      fontSize={{ base: 'md', md: 'lg' }}
                      fontWeight="semibold"
                      variant="outline"
                      colorScheme="blue"
                      onClick={() => navigate('/admin/user')}
                      borderRadius="xl"
                      leftIcon={<Icon as={FiSettings} boxSize={5} />}
                      _hover={{
                        bg: 'blue.50',
                        transform: 'scale(1.01)'
                      }}
                      transition="all 0.2s"
                    >
                      {t('관리자 페이지')}
                    </Button>
                  )}

                  <Button
                    h={{ base: '44px', md: '52px' }}
                    w="full"
                    fontSize={{ base: 'sm', md: 'md' }}
                    variant="ghost"
                    colorScheme="gray"
                    onClick={handleLogout}
                    borderRadius="xl"
                    _hover={{
                      bg: 'gray.100'
                    }}
                  >
                    {t('Logout')}
                  </Button>
                </VStack>
              </VStack>
            </Box>
          ) : (
            /* 로그인 전 - 제목과 설명 포함 */
            <>
              <Box textAlign="center" mb={8}>
                <Text
                  fontSize={{ base: '4xl', md: '5xl' }}
                  fontWeight="bold"
                  color="gray.800"
                  mb={2}
                >
                  FreeRipple
                </Text>
                <Text
                  fontSize={{ base: 'xl', md: '2xl' }}
                  fontWeight="bold"
                  color="purple.600"
                  mb={2}
                >
                  {t('Completely Free XRP Auto Trading')}
                </Text>
                <Text fontSize={{ base: 'md', md: 'lg' }} color="gray.600">
                  {t('Smart trading that starts automatically with just one click')}
                </Text>
              </Box>

              {/* 메인 컨텐츠 카드 */}
              <Box
                bg="white"
                borderRadius="2xl"
                boxShadow="xl"
                p={{ base: 6, md: 8 }}
                mb={6}
              >
                <VStack spacing={6} align="stretch">
                  <Box textAlign="center" py={4}>
                    <Icon as={LockIcon} boxSize={12} color="gray.400" mb={4} />
                    <Text
                      fontSize={{ base: 'xl', md: '2xl' }}
                      fontWeight="bold"
                      color="gray.800"
                      mb={2}
                    >
                      {t('Login Required')}
                    </Text>
                    <Text
                      fontSize={{ base: 'md', md: 'lg' }}
                      color="gray.600"
                      mb={6}
                    >
                      {t('Please login to start auto trading')}
                    </Text>
                    <Button
                      h={{ base: '60px', md: '70px' }}
                      w="full"
                      fontSize={{ base: 'xl', md: '2xl' }}
                      fontWeight="bold"
                      colorScheme="purple"
                      onClick={onLoginOpen}
                      borderRadius="xl"
                      boxShadow="lg"
                      leftIcon={<Icon as={LockIcon} boxSize={6} />}
                      _hover={{
                        transform: 'scale(1.02)',
                        boxShadow: 'xl'
                      }}
                      transition="all 0.2s"
                      mb={4}
                    >
                      {t('Login')}
                    </Button>

                    {/* 가입 문의 안내 */}
                    <Box
                      bg="blue.50"
                      p={4}
                      borderRadius="xl"
                      borderWidth="2px"
                      borderColor="blue.200"
                      textAlign="center"
                    >
                      <Text
                        fontSize={{ base: 'sm', md: 'md' }}
                        color="gray.700"
                        mb={2}
                      >
                        {t("Don't have an account?")}
                      </Text>
                      <Button
                        as="a"
                        href="https://t.me/Lucas_1404"
                        target="_blank"
                        rel="noopener noreferrer"
                        size="md"
                        colorScheme="blue"
                        leftIcon={<Icon as={FaTelegram} boxSize={5} />}
                        borderRadius="lg"
                        _hover={{
                          transform: 'scale(1.05)'
                        }}
                        transition="all 0.2s"
                      >
                        {t('Sign Up via Telegram')}
                      </Button>
                      <Text
                        fontSize={{ base: 'xs', md: 'sm' }}
                        color="gray.500"
                        mt={2}
                      >
                        @Lucas_1404
                      </Text>
                    </Box>
                  </Box>
                </VStack>
              </Box>
            </>
          )}
        </Box>

        {/* 로그인 모달 */}
        <Modal isOpen={isLoginOpen} onClose={onLoginClose} isCentered size="lg">
          <ModalOverlay bg="blackAlpha.600" />
          <ModalContent borderRadius="2xl" mx={4}>
            <ModalHeader
              fontSize={{ base: '2xl', md: '3xl' }}
              fontWeight="bold"
              textAlign="center"
              pb={4}
            >
              {t('Login')}
            </ModalHeader>
            <ModalCloseButton size="lg" />
            <ModalBody pb={6}>
              <VStack spacing={6}>
                <FormControl>
                  <FormLabel
                    fontSize={{ base: 'lg', md: 'xl' }}
                    fontWeight="semibold"
                    mb={3}
                  >
                    {t('Account')}
                  </FormLabel>
                  <Input
                    placeholder={t('Enter your account')}
                    value={account}
                    onChange={(e) => setAccount(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        handleLogin()
                      }
                    }}
                    h={{ base: '56px', md: '64px' }}
                    fontSize={{ base: 'lg', md: 'xl' }}
                    borderRadius="xl"
                    borderWidth="2px"
                    _focus={{
                      borderColor: 'purple.500',
                      boxShadow: '0 0 0 1px var(--chakra-colors-purple-500)'
                    }}
                  />
                </FormControl>
                <FormControl>
                  <FormLabel
                    fontSize={{ base: 'lg', md: 'xl' }}
                    fontWeight="semibold"
                    mb={3}
                  >
                    {t('Password')}
                  </FormLabel>
                  <Input
                    placeholder={t('Enter your password')}
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        handleLogin()
                      }
                    }}
                    h={{ base: '56px', md: '64px' }}
                    fontSize={{ base: 'lg', md: 'xl' }}
                    borderRadius="xl"
                    borderWidth="2px"
                    _focus={{
                      borderColor: 'purple.500',
                      boxShadow: '0 0 0 1px var(--chakra-colors-purple-500)'
                    }}
                  />
                </FormControl>
              </VStack>
            </ModalBody>
            <ModalFooter pt={4} pb={6}>
              <VStack w="full" spacing={3}>
                <Button
                  h={{ base: '56px', md: '64px' }}
                  w="full"
                  fontSize={{ base: 'xl', md: '2xl' }}
                  fontWeight="bold"
                  colorScheme="purple"
                  onClick={handleLogin}
                  isLoading={isLoggingIn}
                  loadingText={t('Logging in...')}
                  borderRadius="xl"
                  leftIcon={<Icon as={LockIcon} boxSize={6} />}
                >
                  {t('Login')}
                </Button>
                <Button
                  h={{ base: '48px', md: '56px' }}
                  w="full"
                  fontSize={{ base: 'md', md: 'lg' }}
                  variant="ghost"
                  onClick={onLoginClose}
                  isDisabled={isLoggingIn}
                  borderRadius="xl"
                >
                  {t('Cancel')}
                </Button>
              </VStack>
            </ModalFooter>
          </ModalContent>
        </Modal>

        {/* 시드 입력 모달 (관리자는 스위치 옵션 포함) */}
        <Modal
          isOpen={isSeedOpen}
          onClose={onSeedClose}
          isCentered
          size={useManualConfig && isAdmin ? "2xl" : "md"}
        >
          <ModalOverlay bg="blackAlpha.600" />
          <ModalContent borderRadius="2xl" mx={4} maxH="90vh" overflow="hidden">
            <ModalHeader
              fontSize={{ base: 'xl', md: '2xl' }}
              fontWeight="bold"
              textAlign="center"
              pb={2}
            >
              {t('Start Auto Trading')}
            </ModalHeader>
            <ModalCloseButton size="lg" />
            <ModalBody pb={4} overflowY="auto">
              <VStack spacing={4} align="stretch">
                {/* 관리자용 스위치 */}
                {isAdmin && (
                  <Box
                    p={3}
                    bg="purple.50"
                    borderRadius="xl"
                    borderWidth="2px"
                    borderColor="purple.200"
                  >
                    <HStack justify="space-between">
                      <VStack align="start" spacing={0}>
                        <Text fontWeight="bold" color="purple.700">
                          {t('Manual Config Mode')}
                        </Text>
                        <Text fontSize="xs" color="gray.600">
                          {t('Manually set leverage, DCA, etc.')}
                        </Text>
                      </VStack>
                      <Switch
                        size="lg"
                        colorScheme="purple"
                        isChecked={useManualConfig}
                        onChange={(e) => setUseManualConfig(e.target.checked)}
                      />
                    </HStack>
                  </Box>
                )}

                {/* 수동 설정 모드 */}
                {useManualConfig && isAdmin ? (
                  <VStack spacing={4} align="stretch">
                    <FormControl>
                      <FormLabel fontSize="md" fontWeight="semibold">
                        {t('Leverage')}
                      </FormLabel>
                      <Input
                        type="number"
                        value={manualLeverage}
                        onChange={(e) => setManualLeverage(e.target.value)}
                        h="52px"
                        fontSize="md"
                        borderRadius="xl"
                        borderWidth="2px"
                      />
                    </FormControl>

                    <FormControl>
                      <FormLabel fontSize="md" fontWeight="semibold">
                        {t('Take Profit (%)')}
                      </FormLabel>
                      <Input
                        type="number"
                        value={manualTakeProfit}
                        onChange={(e) => setManualTakeProfit(e.target.value)}
                        h="52px"
                        fontSize="md"
                        borderRadius="xl"
                        borderWidth="2px"
                      />
                    </FormControl>

                    <FormControl>
                      <FormLabel fontSize="md" fontWeight="semibold">
                        {t('Stop Loss (%)')}
                      </FormLabel>
                      <Input
                        type="number"
                        value={manualStopLoss}
                        onChange={(e) => setManualStopLoss(e.target.value)}
                        h="52px"
                        fontSize="md"
                        borderRadius="xl"
                        borderWidth="2px"
                      />
                    </FormControl>

                    <FormControl>
                      <HStack justify="space-between" mb={2}>
                        <FormLabel fontSize="md" fontWeight="semibold" mb={0}>
                          {t('Size')}
                        </FormLabel>
                        <Button
                          size="xs"
                          variant={showCalculator ? 'solid' : 'outline'}
                          colorScheme="purple"
                          onClick={() => {
                            if (!showCalculator) {
                              // 계산기 열 때 현재 물타기 회수로 동기화
                              setCalcDivide(manualDcaCount)
                            }
                            setShowCalculator(!showCalculator)
                          }}
                        >
                          {showCalculator ? t('Close Calculator') : t('Open Calculator')}
                        </Button>
                      </HStack>
                      <Input
                        type="number"
                        value={manualSize}
                        onChange={(e) => setManualSize(e.target.value)}
                        h="52px"
                        fontSize="md"
                        borderRadius="xl"
                        borderWidth="2px"
                      />
                      <Text fontSize="xs" color="gray.500" mt={1}>
                        1 size = 10 XRP
                      </Text>

                      {/* 사이즈 계산기 */}
                      {showCalculator && (
                        <VStack
                          mt={3}
                          p={4}
                          bg="purple.50"
                          borderRadius="xl"
                          borderWidth="2px"
                          borderColor="purple.200"
                          spacing={3}
                          align="stretch"
                        >
                          <Text fontSize="sm" fontWeight="bold" color="purple.700">
                            📊 {t('Size Calculator')}
                          </Text>

                          <FormControl>
                            <FormLabel fontSize="xs" mb={1}>
                              {t('XRP Current Price')}
                            </FormLabel>
                            <Input
                              type="number"
                              value={xrpPrice}
                              isReadOnly
                              bg="gray.100"
                              size="sm"
                              fontSize="sm"
                            />
                          </FormControl>

                          <FormControl>
                            <FormLabel fontSize="xs" mb={1}>
                              {t('Total Seed (USDT)')}
                            </FormLabel>
                            <Input
                              type="number"
                              value={calcSeed}
                              onChange={(e) => setCalcSeed(e.target.value)}
                              size="sm"
                              fontSize="sm"
                              placeholder="1000"
                            />
                          </FormControl>

                          <FormControl>
                            <FormLabel fontSize="xs" mb={1}>
                              {t('Seed Usage Ratio: {{percent}}%', { percent: calcSeedPercent })}
                            </FormLabel>
                            <HStack spacing={2}>
                              <Slider
                                value={calcSeedPercent}
                                onChange={(val) => setCalcSeedPercent(val)}
                                min={1}
                                max={100}
                                step={1}
                                flex="1"
                                colorScheme="purple"
                              >
                                <SliderTrack>
                                  <SliderFilledTrack />
                                </SliderTrack>
                                <SliderThumb />
                              </Slider>
                              <Input
                                type="number"
                                value={calcSeedPercent}
                                onChange={(e) => setCalcSeedPercent(Number(e.target.value))}
                                min={1}
                                max={100}
                                w="60px"
                                size="sm"
                                fontSize="sm"
                              />
                            </HStack>
                            <HStack mt={1} spacing={1} flexWrap="wrap">
                              {[10, 20, 30, 40, 50, 60, 70, 80, 90, 100].map((percent) => (
                                <Button
                                  key={percent}
                                  size="xs"
                                  variant={calcSeedPercent === percent ? 'solid' : 'outline'}
                                  colorScheme={calcSeedPercent === percent ? 'purple' : 'gray'}
                                  onClick={() => setCalcSeedPercent(percent)}
                                  fontSize="xs"
                                  h="20px"
                                  minW="32px"
                                >
                                  {percent}%
                                </Button>
                              ))}
                            </HStack>
                            <Text fontSize="xs" color="gray.600" mt={1}>
                              {t('Seed to use:')} ${((Number(calcSeed) || 0) * (calcSeedPercent / 100)).toFixed(2)}
                            </Text>
                          </FormControl>

                          <FormControl>
                            <FormLabel fontSize="xs" mb={1}>
                              {t('Division Count (DCA Count)')}
                            </FormLabel>
                            <Input
                              type="number"
                              value={calcDivide}
                              onChange={(e) => setCalcDivide(e.target.value)}
                              size="sm"
                              fontSize="sm"
                              placeholder="50"
                            />
                          </FormControl>

                          <Box
                            p={3}
                            bg="white"
                            borderRadius="lg"
                            borderWidth="1px"
                            borderColor="purple.300"
                          >
                            <VStack spacing={1} align="stretch">
                              <HStack justify="space-between" fontSize="xs">
                                <Text color="gray.600">{t('Entry Amount per Trade:')}</Text>
                                <Text fontWeight="semibold">
                                  ${(((Number(calcSeed) || 0) * (calcSeedPercent / 100)) / (Number(calcDivide) || 1)).toFixed(2)}
                                </Text>
                              </HStack>
                              <HStack justify="space-between" fontSize="xs">
                                <Text color="gray.600">{t('With Leverage:')}</Text>
                                <Text fontWeight="semibold">
                                  ${((((Number(calcSeed) || 0) * (calcSeedPercent / 100)) / (Number(calcDivide) || 1)) * (Number(manualLeverage) || 1)).toFixed(2)}
                                </Text>
                              </HStack>
                              <HStack justify="space-between" fontSize="xs">
                                <Text color="gray.600">{t('Coin Amount:')}</Text>
                                <Text fontWeight="semibold">
                                  {Math.floor(((((Number(calcSeed) || 0) * (calcSeedPercent / 100)) / (Number(calcDivide) || 1)) * (Number(manualLeverage) || 1)) / (Number(xrpPrice) || 1))} XRP
                                </Text>
                              </HStack>
                              <Divider my={1} />
                              <HStack justify="space-between">
                                <Text fontSize="sm" fontWeight="bold" color="purple.700">
                                  {t('Calculated Size:')}
                                </Text>
                                <Text fontSize="lg" fontWeight="bold" color="purple.600">
                                  {calculatedSize} size
                                </Text>
                              </HStack>
                            </VStack>
                          </Box>

                          <Button
                            size="sm"
                            colorScheme="purple"
                            onClick={handleApplyCalculatedSize}
                            isDisabled={calculatedSize === 0}
                          >
                            {t('Apply Calculated Size')}
                          </Button>
                        </VStack>
                      )}
                    </FormControl>

                    <FormControl>
                      <FormLabel fontSize="md" fontWeight="semibold">
                        {t('DCA Count')}
                      </FormLabel>
                      <Input
                        type="number"
                        value={manualDcaCount}
                        onChange={(e) => setManualDcaCount(e.target.value)}
                        h="52px"
                        fontSize="md"
                        borderRadius="xl"
                        borderWidth="2px"
                      />
                    </FormControl>

                    <FormControl>
                      <FormLabel fontSize="md" fontWeight="semibold">
                        {t('ROI 트리거 (%)')}
                      </FormLabel>
                      <Input
                        type="number"
                        value={manualRoi}
                        onChange={(e) => setManualRoi(e.target.value)}
                        h="52px"
                        fontSize="md"
                        borderRadius="xl"
                        borderWidth="2px"
                        placeholder="-30"
                      />
                    </FormControl>

                    <Divider my={4} />

                    {/* 고급 물타기 설정 */}
                    <Box>
                      <HStack justify="space-between" mb={3}>
                        <Text fontSize="md" fontWeight="bold">
                          {t('DCA Strategy (Advanced Settings)')}
                        </Text>
                        <HStack spacing={2}>
                          <Button
                            size="xs"
                            variant="outline"
                            colorScheme="red"
                            onClick={() => setManualRoiTriggers([])}
                          >
                            {t('Reset')}
                          </Button>
                          <Button 
                            size="xs" 
                            variant="outline"
                            colorScheme="purple"
                            onClick={handleBatchSetupManual}
                          >
                            {t('Batch Create')}
                          </Button>
                        </HStack>
                      </HStack>

                      <VStack align="stretch" spacing={3}>
                        {/* 헤더 */}
                        <Box
                          display="grid"
                          gridTemplateColumns="60px 1fr 1fr 32px"
                          px={1}
                          fontWeight="semibold"
                          color="gray.600"
                          fontSize="xs"
                        >
                          <Text textAlign="center">{t('Round')}</Text>
                          <Text textAlign="center">ROI (%)</Text>
                          <Text textAlign="center">{t('Size')}</Text>
                          <Box />
                        </Box>

                        {/* 물타기 단계 목록 */}
                        <Box maxH="250px" overflowY="auto" pr={1}>
                          {manualRoiTriggers.length === 0 ? (
                            <Box textAlign="center" color="gray.500" py={3} fontSize="sm">
                              {t('DCA not in use.')}
                              <br />
                              <Text fontSize="xs" color="gray.400" mt={1}>
                                {t('Click Batch Create or Add button')}
                              </Text>
                            </Box>
                          ) : (
                            manualRoiTriggers.map((item, index) => (
                              <Box
                                key={index}
                                display="grid"
                                gridTemplateColumns="60px 1fr 1fr 32px"
                                gap={2}
                                alignItems="center"
                                mb={2}
                              >
                                {/* 회차 */}
                                <Text textAlign="center" fontSize="xs">
                                  {index + 1}
                                </Text>

                                {/* ROI 입력 */}
                                <Input
                                  type="number"
                                  size="sm"
                                  value={item.roi}
                                  onChange={(e) => {
                                    const updated = [...manualRoiTriggers]
                                    updated[index].roi = e.target.value
                                    setManualRoiTriggers(updated)
                                  }}
                                  onBlur={() => {
                                    if (!item.roi || Number(item.roi) === 0) {
                                      const updated = [...manualRoiTriggers]
                                      updated[index].roi = '-30'
                                      setManualRoiTriggers(updated)
                                    }
                                  }}
                                  placeholder="ROI"
                                  fontSize="xs"
                                />

                                {/* 사이즈 입력 */}
                                <Input
                                  type="number"
                                  size="sm"
                                  value={item.size}
                                  onChange={(e) => {
                                    const updated = [...manualRoiTriggers]
                                    updated[index].size = e.target.value
                                    setManualRoiTriggers(updated)
                                  }}
                                  onBlur={() => {
                                    if (!item.size || Number(item.size) < 1) {
                                      const updated = [...manualRoiTriggers]
                                      updated[index].size = '1'
                                      setManualRoiTriggers(updated)
                                    }
                                  }}
                                  placeholder={t('사이즈') as string}
                                  fontSize="xs"
                                />

                                {/* 삭제 버튼 */}
                                <IconButton
                                  aria-label="delete"
                                  icon={<CloseIcon />}
                                  size="xs"
                                  onClick={() => removeManualRoiTrigger(index)}
                                  variant="ghost"
                                  colorScheme="red"
                                />
                              </Box>
                            ))
                          )}
                        </Box>

                        <Button
                          leftIcon={<AddIcon />}
                          w="100%"
                          size="sm"
                          onClick={addManualRoiTrigger}
                          colorScheme="purple"
                          variant="outline"
                        >
                          {t('Add DCA Step')}
                        </Button>
                      </VStack>
                    </Box>

                    <Divider my={4} />

                    <Box
                      bg="purple.50"
                      p={3}
                      borderRadius="xl"
                      borderWidth="2px"
                      borderColor="purple.200"
                    >
                      <Text fontSize="xs" color="gray.700">
                        <Text as="span" fontWeight="bold">Settings Summary:</Text>
                        <br />
                        • Leverage: {manualLeverage}x
                        <br />
                        • Take Profit: {manualTakeProfit}%
                        <br />
                        • Stop Loss: {manualStopLoss}%
                        <br />
                        • Size: {manualSize} (= {Number(manualSize) * 10} XRP)
                        <br />
                        • DCA Steps: {manualRoiTriggers.length}
                      </Text>
                    </Box>
                  </VStack>
                ) : (
                  /* 시드 입력 모드 */
                  <>
                    <FormControl>
                      <FormLabel
                        fontSize={{ base: 'md', md: 'lg' }}
                        fontWeight="semibold"
                        mb={2}
                      >
                        {t('Total Seed Amount (USDT)')}
                      </FormLabel>
                      <Input
                        type="number"
                        placeholder={t('e.g.: 1000')}
                        value={seedAmount}
                        onChange={(e) => setSeedAmount(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            handleConfirmStartWithSeed()
                          }
                        }}
                        h={{ base: '52px', md: '56px' }}
                        fontSize={{ base: 'md', md: 'lg' }}
                        borderRadius="xl"
                        borderWidth="2px"
                        _focus={{
                          borderColor: 'green.500',
                          boxShadow: '0 0 0 1px var(--chakra-colors-green-500)'
                        }}
                      />
                    </FormControl>

                    {seedAmount &&
                      !isNaN(Number(seedAmount)) &&
                      Number(seedAmount) > 0 && (
                        <Box
                          bg="green.50"
                          p={3}
                          borderRadius="lg"
                          w="full"
                          borderWidth="1px"
                          borderColor="green.200"
                        >
                          <HStack justify="space-between" fontSize="sm">
                            <Text fontWeight="semibold" color="gray.700">
                              {t('Seed Used:')}
                            </Text>
                            <Text fontWeight="bold" color="green.700">
                              ${(Number(seedAmount) * 0.2).toFixed(2)}
                            </Text>
                          </HStack>
                          <HStack justify="space-between" fontSize="sm" mt={1}>
                            <Text fontWeight="semibold" color="gray.700">
                              {t('Margin per Entry:')}
                            </Text>
                            <Text fontWeight="bold" color="green.700">
                              ${((Number(seedAmount) * 0.2) / 100).toFixed(2)}
                            </Text>
                          </HStack>
                        </Box>
                      )}
                  </>
                )}
              </VStack>
            </ModalBody>
            <ModalFooter pt={2} pb={4}>
              <VStack w="full" spacing={3}>
                <Button
                  h={{ base: '56px', md: '64px' }}
                  w="full"
                  fontSize={{ base: 'xl', md: '2xl' }}
                  fontWeight="bold"
                  colorScheme={useManualConfig && isAdmin ? 'purple' : 'green'}
                  onClick={useManualConfig && isAdmin ? handleConfirmStartWithManual : handleConfirmStartWithSeed}
                  isLoading={isTradingLoading}
                  loadingText={t('시작 중...')}
                  borderRadius="xl"
                  leftIcon={<Icon as={CheckCircleIcon} boxSize={6} />}
                >
                  {t('Start Auto Trading')}
                </Button>
                <Button
                  h={{ base: '48px', md: '56px' }}
                  w="full"
                  fontSize={{ base: 'md', md: 'lg' }}
                  variant="ghost"
                  onClick={onSeedClose}
                  isDisabled={isTradingLoading}
                  borderRadius="xl"
                >
                  {t('Cancel')}
                </Button>
              </VStack>
            </ModalFooter>
          </ModalContent>
        </Modal>

        {/* API 관리 모달 */}
        <Modal isOpen={isApiOpen} onClose={onApiClose} isCentered size="lg">
          <ModalOverlay bg="blackAlpha.600" />
          <ModalContent borderRadius="2xl" mx={4}>
            <ModalHeader
              fontSize={{ base: '2xl', md: '3xl' }}
              fontWeight="bold"
              textAlign="center"
              pb={4}
            >
              {t('API Key Management')}
            </ModalHeader>
            <ModalCloseButton size="lg" />
            <ModalBody pb={6}>
              <VStack spacing={6} align="stretch">
                <Box mb={4}>
                  <Text
                    fontSize={{ base: 'md', md: 'lg' }}
                    color="gray.700"
                    mb={4}
                    textAlign="center"
                  >
                    {t('Is this your first time registering an API key?')}
                  </Text>
                  <ApiManual />
                </Box>

                <Divider />

                <FormControl>
                  <FormLabel
                    fontSize={{ base: 'lg', md: 'xl' }}
                    fontWeight="semibold"
                    mb={3}
                  >
                    {t('API Key (키)')}
                  </FormLabel>
                  <Input
                    placeholder={t('Enter the API Key from your exchange')}
                    value={apiKey}
                    onChange={(e) => setApiKey(e.target.value)}
                    h={{ base: '56px', md: '64px' }}
                    fontSize={{ base: 'lg', md: 'xl' }}
                    borderRadius="xl"
                    borderWidth="2px"
                    _focus={{
                      borderColor: 'purple.500',
                      boxShadow: '0 0 0 1px var(--chakra-colors-purple-500)'
                    }}
                  />
                </FormControl>

                <FormControl>
                  <FormLabel
                    fontSize={{ base: 'lg', md: 'xl' }}
                    fontWeight="semibold"
                    mb={3}
                  >
                    {t('API Secret')}
                  </FormLabel>
                  <InputGroup size="lg">
                    <Input
                      type={showSecret ? 'text' : 'password'}
                      placeholder={t(
                        'Enter the API Secret from your exchange'
                      )}
                      value={apiSecret}
                      onChange={(e) => setApiSecret(e.target.value)}
                      h={{ base: '56px', md: '64px' }}
                      fontSize={{ base: 'lg', md: 'xl' }}
                      borderRadius="xl"
                      borderWidth="2px"
                      _focus={{
                        borderColor: 'purple.500',
                        boxShadow: '0 0 0 1px var(--chakra-colors-purple-500)'
                      }}
                    />
                    <InputRightElement
                      width="4.5rem"
                      h="full"
                      alignItems="center"
                      mr={2}
                    >
                      <Button
                        h="1.75rem"
                        size="md"
                        onClick={() => setShowSecret(!showSecret)}
                        variant={'ghost'}
                      >
                        {showSecret ? <ViewOffIcon /> : <ViewIcon />}
                      </Button>
                    </InputRightElement>
                  </InputGroup>
                </FormControl>
              </VStack>
            </ModalBody>
            <ModalFooter pt={4} pb={6}>
              <VStack w="full" spacing={3}>
                <Button
                  h={{ base: '56px', md: '64px' }}
                  w="full"
                  fontSize={{ base: 'xl', md: '2xl' }}
                  fontWeight="bold"
                  colorScheme="purple"
                  onClick={handleSaveApi}
                  borderRadius="xl"
                  leftIcon={<Icon as={SettingsIcon} boxSize={6} />}
                >
                  {t('Save')}
                </Button>
                <Button
                  h={{ base: '48px', md: '56px' }}
                  w="full"
                  fontSize={{ base: 'md', md: 'lg' }}
                  variant="ghost"
                  onClick={onApiClose}
                  borderRadius="xl"
                >
                  {t('Cancel')}
                </Button>
              </VStack>
            </ModalFooter>
          </ModalContent>
        </Modal>
      </Box>
    </DefaultLayout>
  )
}

export default Home
